import { logger } from "./logger";
// Date formatting utilities

// Format date to input default (YYYY-MM-DD)
export const formatDateForInput = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch (error) {
    logger.warn("Error formatting date:", dateString, error);
    return "";
  }
};

// An API timestamp as a Date. The "YYYY-MM-DD HH:mm:ss" form is UTC without a zone
// marker, which browsers would otherwise read as local time or reject.
export const parseApiDate = (dateString: string): Date => {
  const zoneless = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/.exec(dateString);
  return new Date(zoneless ? `${zoneless[1]}T${zoneless[2]}Z` : dateString);
};

// Calendar day of an API date field, e.g. "Aug 4, 2026". Formatted in UTC
export const formatDateLong = (
  dateString: string | null | undefined,
  fallback: string = "—"
): string => {
  if (!dateString) return fallback;

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  const date = dateOnly
    ? new Date(
        Date.UTC(
          Number(dateOnly[1]),
          Number(dateOnly[2]) - 1,
          Number(dateOnly[3])
        )
      )
    : new Date(dateString);

  if (isNaN(date.getTime())) return fallback;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

// Format date for display (MM/DD/YYYY)
export const formatDateForDisplay = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return "Not provided";

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    return `${month}/${day}/${year}`;
  } catch (error) {
    logger.warn("Error formatting date for display:", dateString, error);
    return "Invalid date";
  }
};

// Timestamp with ordinal day, e.g. "September 2nd, 2026, 1:46 PM". Local time.
export const formatDateTimeWithOrdinal = (dateString: string): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "Invalid date";

  const formatted = new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);

  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";

  // The day is the first number in the en-US long format.
  return formatted.replace(/\d+/, `${day}${suffix}`);
};
